import { ReasonInput } from '@strata/react-compound-inputs';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { handleError } from 'services/Error';
import { useGetNeedsByDateQuery, useUpdateNeedsByDateMutation } from 'store/api/performanceApi';

const Title = 'Update Next Fill Needs By Date';

const ReasonOptions = [
    'Discontinued therapy',
    'Drug holiday',
    'Future service start date',
    'Medication on hand',
    'Termed coverage',
];

export const UpdateNeedsByDateDialog = ({ isOpen, onClose, treatmentDetails = undefined }) => {
    const { payerId, treatmentId, activeReferral } = treatmentDetails ?? {};
    const { data: needsByDateData } = useGetNeedsByDateQuery(
        { treatmentId },
        {
            skip: !treatmentId,
        },
    );
    const [updateNeedsByDate] = useUpdateNeedsByDateMutation();
    return (
        <Formik
            initialValues={{
                needsByDate: needsByDateData?.needsByDate,
                ...ReasonInput.initialValues,
            }}
            validationSchema={Yup.object({
                needsByDate: DateInputFormik.validator().required(),
                ...ReasonInput.validationSchema,
            })}
            enableReinitialize
            validateOnMount
            onSubmit={async (values, { resetForm }) => {
                try {
                    await updateNeedsByDate({
                        treatmentId,
                        body: {
                            payer_id: payerId,
                            referral_id: activeReferral.referralId,
                            needs_by_date: values.needsByDate,
                            ...ReasonInput.submitValues(values),
                        },
                    }).unwrap();
                    SuccessToast({ message: 'Needs by date updated successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to update needs by date. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={600}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Update"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            <DateInputFormik
                                id="needsByDate"
                                label="Next Fill Needs By Date"
                                required
                            />
                            <ReasonInput reasons={ReasonOptions} />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

UpdateNeedsByDateDialog.Title = Title;

UpdateNeedsByDateDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    treatmentDetails: PropTypes.shape({
        payerId: PropTypes.string,
        treatmentId: PropTypes.string,
        needsByDate: PropTypes.string,
        activeReferral: PropTypes.shape({
            referralId: PropTypes.string,
        }),
    }),
};
